import * as React from 'react';

import { useLocalizedContent } from '@coursera-web/internationalization-lib';
import * as Sentry from '@sentry/react';

import { useLocation, useParams } from 'js/lib/useRouter';

import { AVAILABLE_SPACES } from 'bundles/cms/utils/SpaceUtils';
import { NotFound } from 'bundles/common/components/withNotFound';
import type { CareerAcademyRolePageFieldsFragment } from 'bundles/unified-career-academy/__generated__/contentfulTypes';
import ConsumerPathwayRolePageDataContext from 'bundles/unified-career-academy/contexts/ConsumerPathwayRolePageDataContext';
import useCareerRoleData from 'bundles/unified-career-academy/hooks/useCareerRoleData';
import ConsumerPathwayRelatedRolesProvider from 'bundles/unified-career-academy/providers/ConsumerPathwayRelatedRolesProvider';
import ConsumerPathwayRoleRelatedDegreeCollectionProvider from 'bundles/unified-career-academy/providers/ConsumerPathwayRoleRelatedDegreeCollectionProvider';
import EventingDataProvider from 'bundles/unified-career-academy/providers/EventingDataProvider';
import { CareerAcademyRolePageFragmentDoc } from 'bundles/unified-career-academy/queries/contentfulCareerAcademyRoleQuery';

type Props = {
  children: React.ReactNode;
};

const ConsumerPathwayRolePageDataProvider = ({ children }: Props) => {
  const { roleSlug } = useParams();
  const { query } = useLocation();
  const isPreview = query.preview === 'true';

  const {
    data: pageContent,
    loading: pageContentLoading,
    error: pageContentError,
  } = useLocalizedContent<CareerAcademyRolePageFieldsFragment>({
    isPreview,
    contentfulSpace: AVAILABLE_SPACES.marketing.toLowerCase(),
    contentType: 'careerAcademyRole',
    fragmentDoc: CareerAcademyRolePageFragmentDoc,
    fragmentFieldsName: 'CareerAcademyRolePageFields',
    filters: {
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      slug: roleSlug,
    },
    ssr: true,
    skipQuery: !roleSlug,
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching localized content for Role Description Page',
        },
      }),
  });

  const { data: careerRoleData, loading: careerRoleDataLoading } = useCareerRoleData({
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    slug: roleSlug,
    ssr: true,
  });

  if (pageContentError) {
    return <NotFound />;
  }

  return (
    <ConsumerPathwayRolePageDataContext.Provider
      value={{
        contentfulContent: pageContent,
        careerRoleData,
        loading: pageContentLoading || careerRoleDataLoading,
      }}
    >
      <ConsumerPathwayRelatedRolesProvider>
        <ConsumerPathwayRoleRelatedDegreeCollectionProvider>
          {/*
           // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
          <EventingDataProvider roleSlug={roleSlug}>{children}</EventingDataProvider>
        </ConsumerPathwayRoleRelatedDegreeCollectionProvider>
      </ConsumerPathwayRelatedRolesProvider>
    </ConsumerPathwayRolePageDataContext.Provider>
  );
};

export default ConsumerPathwayRolePageDataProvider;
